import { IconButton, Tooltip } from '@sweep-io/sweep-design';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { Settings } from '@sweep-io/sweep-design/dist/icons';
import usePopover from '../../../hooks/usePopover';
import { MenuItem, Popover, Stack } from '@mui/material';
import { useState } from 'react';
import { InstructionsDialog } from './instructions/InstructionsDialog';
import usePermission from '../../common/permissions/usePermission';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { Permission } from '@server/permissions';

interface SettingsWrapperProps {
  setAiAssistantDialogOpen: (open: boolean) => void;
  isCollapsed?: boolean;
}

interface SettingsMenuItem {
  label: string;
  onClick: () => void;
  restrictedText?: string;
  permission?: Permission[];
  isDisabled?: boolean;
}

export const SettingsWrapper = ({
  setAiAssistantDialogOpen,
  isCollapsed,
}: SettingsWrapperProps) => {
  const { supportAgentInAgenticCenter } = useFeatureToggle();
  const [isInstructionsDialogOpen, setIsInstructionsDialogOpen] = useState(false);
  const [isInstructionsAllowed] = usePermission(['create:ai-sessions']);
  const { openPopover, closePopover, isPopoverOpen, anchorElement, anchorRef } = usePopover();

  const menuItems: SettingsMenuItem[] = [
    {
      label: 'Salesforce apps',
      onClick: () => {
        setAiAssistantDialogOpen(true);
      },
    },
    {
      label: 'Instructions',
      onClick: () => setIsInstructionsDialogOpen(true),
      permission: ['create:ai-sessions'],
      restrictedText: 'To see instructions, please contact your admin',
      isDisabled: !isInstructionsAllowed,
    },
  ];

  const renderMenuItem = (item: SettingsMenuItem, isDisabled?: boolean) => (
    <MenuItem
      key={item.label}
      disabled={isDisabled}
      onClick={() => {
        item.onClick();
        closePopover();
      }}
    >
      {item.label}
    </MenuItem>
  );

  return (
    <>
      {isInstructionsDialogOpen && (
        <InstructionsDialog onClose={() => setIsInstructionsDialogOpen(false)} />
      )}

      <Tooltip title="Settings">
        <IconButton
          onClick={() => {
            if (supportAgentInAgenticCenter) {
              setAiAssistantDialogOpen(true);
            } else {
              openPopover();
            }
          }}
          size={isCollapsed ? 'tiny' : 'small'}
          variant="system"
          ref={anchorRef}
        >
          <Settings />
        </IconButton>
      </Tooltip>

      <Popover
        open={isPopoverOpen}
        onClose={closePopover}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack p={1} sx={{ width: '276px' }}>
          {menuItems.map((item) => {
            if (item.permission) {
              return (
                <RestrictedTooltip
                  notAllowedTitle={item.restrictedText}
                  to={item.permission}
                  key={item.label}
                >
                  {renderMenuItem(item, item.isDisabled)}
                </RestrictedTooltip>
              );
            }
            return renderMenuItem(item, item.isDisabled);
          })}
        </Stack>
      </Popover>
    </>
  );
};
